// **The leaderboard screen** — the second of Home's two CTAs (`home-and-single-player`; it was the
// third until WAG-108 removed the computer match's CTA from Home).
//
// A **document, not a canvas**, in `Standings.tsx`'s mould: paper ground, no world view, no
// transport of its own. There is no `Table`/`Board`/`List` component in the kit, so it is hand-rolled
// from tokens exactly as `Standings` and `HostLeaderboard` are, over
// `prototype/ui_kits/waggle-app/standings.html`'s `.table`/`.thead`/`.tr` grid.
//
// Three rules the implementation is shaped around, each one a thing a plausible version gets wrong:
//
//   * **The order is the server's.** `entries` arrives highest score first and is rendered in that
//     order, full stop. A `sort()` here would agree with the server every correct day and conceal the
//     one day it did not.
//   * **A `self`-scope board is relabelled, never hidden.** With authentication off every run
//     collapses onto one sentinel player, so the board is genuinely one row — of the player's *own*
//     bests. Saying so is what stops a one-row board reading as a broken feature, and keeping it on
//     screen is what keeps the feature exercised in local development.
//   * **Opening an entry reveals its prompt, and no notice apologises for that.** Sharing the prompt
//     is the point — a good idea spreads without handing over the script — and it is told to the room
//     verbally at the presentation rather than as chrome on every row.
//
// Presentational, like every other route: the read is an injected seam with an inert default, so a
// bare mount renders a real, honest state rather than crashing or fabricating a board.

import { useEffect, useState, type CSSProperties } from 'react';
import type { LeaderboardEntry, LeaderboardOutcome } from '../../net/rounds-leaderboard';

export interface LeaderboardProps {
  /** One read of the board. Absent renders the inert "not connected" state. */
  read?: () => Promise<LeaderboardOutcome>;
  onBack?(): void;
}

/** A bare mount reaches nothing and says so, rather than showing a board of fabricated zeros. */
const INERT_READ = async (): Promise<LeaderboardOutcome> => ({
  kind: 'unavailable',
  message: 'The leaderboard isn’t connected on this screen.',
});

/** `1st` · `2nd` · `3rd` · `11th`. The same rule `Standings` uses, so a placement reads the same on
 * both boards. */
function placement(index: number): string {
  const n = index + 1;
  if (n % 100 >= 11 && n % 100 <= 13) {
    return `${n}th`;
  }
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
}

/** What the script is called: the agent's chosen name, or the stable never-reused ordinal in the
 * same `#7` form the version selector uses — so the two surfaces name one script one way. */
function scriptLabel(entry: LeaderboardEntry): string {
  return entry.scriptName !== null && entry.scriptName !== '' ? entry.scriptName : `#${entry.documentOrdinal}`;
}

/** Scores are recorded as floats; a trailing `.0` on the board is noise, a real fraction is not. */
function scoreLabel(score: number): string {
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

export function Leaderboard({ read = INERT_READ, onBack }: LeaderboardProps = {}) {
  const [outcome, setOutcome] = useState<LeaderboardOutcome | null>(null);
  /** Which entry has its prompt open, by player. One at a time: the board is a ranking first. */
  const [opened, setOpened] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setOutcome(null);
    void read().then((result) => {
      if (!cancelled) {
        setOutcome(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [read]);

  const board = outcome !== null && outcome.kind === 'read' ? outcome.board : null;

  return (
    <div data-testid="leaderboard-screen" style={SCREEN_STYLE}>
      <div style={HEADER_STYLE}>
        <span style={KICKER_STYLE}>
          Leaderboard{board !== null ? ` · seed ${board.seed}` : ''}
        </span>
        <h2 style={TITLE_STYLE}>Best of the meadow</h2>
        {board !== null && (
          <p data-testid="leaderboard-scope" style={SUBTITLE_STYLE}>
            {board.scope === 'global'
              ? 'Everyone’s best sandbox run on this seed. Tap a script to read the prompt behind it.'
              : 'Your own best sandbox runs on this seed — this server isn’t signing anyone in, so every run here is yours. Tap a script to read the prompt behind it.'}
          </p>
        )}
      </div>

      <div style={BODY_STYLE}>
        {outcome === null && (
          <p data-testid="leaderboard-loading" style={NOTE_STYLE}>
            Reading the leaderboard…
          </p>
        )}

        {outcome !== null && outcome.kind === 'unavailable' && (
          <div data-testid="leaderboard-failed" role="alert" style={ALERT_STYLE}>
            <b style={{ fontWeight: 800 }}>The leaderboard couldn’t be read.</b>
            <span>{outcome.message}</span>
          </div>
        )}

        {/* **Empty and unreadable are two different facts**, said differently: "nobody has scored
            yet" is a real state of a working board. */}
        {board !== null && board.entries.length === 0 && (
          <div data-testid="leaderboard-empty" style={EMPTY_STATE_STYLE}>
            <span style={EMPTY_STATE_PILL_STYLE}>
              No scores on this seed yet — the first run in the sandbox takes the top spot.
            </span>
          </div>
        )}

        {board !== null && board.entries.length > 0 && (
          <div style={TABLE_STYLE}>
            <div style={TABLE_HEAD_ROW_STYLE}>
              <span style={{ ...COL_HEAD_STYLE, width: 34, flexShrink: 0 }} />
              <span style={{ ...COL_HEAD_STYLE, flex: 1, minWidth: 0 }}>Player · script</span>
              <span style={{ ...COL_HEAD_STYLE, width: 64, flexShrink: 0, textAlign: 'right' }}>
                Score
              </span>
            </div>
            {/* Rendered in the order received. There is no `sort` here, deliberately. */}
            {board.entries.map((entry, index) => {
              const isOpen = opened === entry.player;
              return (
                <div key={entry.player}>
                  <button
                    type="button"
                    data-testid={`leaderboard-entry-${entry.player}`}
                    aria-expanded={isOpen}
                    onClick={() => setOpened(isOpen ? null : entry.player)}
                    style={{ ...TABLE_ROW_STYLE, background: isOpen ? 'var(--accent-soft)' : 'transparent' }}
                  >
                    <span style={PLACEMENT_STYLE}>{placement(index)}</span>
                    <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                      <b style={NAME_STYLE}>{entry.displayName}</b>
                      <small style={SCRIPT_STYLE}>{scriptLabel(entry)}</small>
                    </span>
                    <span style={SCORE_STYLE}>{scoreLabel(entry.score)}</span>
                  </button>
                  {isOpen && (
                    <div data-testid={`leaderboard-prompt-${entry.player}`} style={PROMPT_STYLE}>
                      {/* A script nobody prompted says so. An empty panel would read as a prompt
                          that failed to load, which is a different and false fact. */}
                      {entry.prompt !== null && entry.prompt !== ''
                        ? `“${entry.prompt}”`
                        : 'No prompt on record for this script — it was written by hand.'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {onBack !== undefined && (
        <div style={BACK_BAR_STYLE}>
          <button type="button" data-testid="leaderboard-back" style={BACK_BUTTON_STYLE} onClick={onBack}>
            Back
          </button>
        </div>
      )}
    </div>
  );
}

// --- styles — the kit's tokens, the same set `Standings` uses -----------------------------

const SCREEN_STYLE: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--surface-app)',
};

const HEADER_STYLE: CSSProperties = {
  padding: '14px 14px 10px',
  background: 'var(--hud-bg)',
  boxShadow: 'inset 0 -1px 0 var(--line)',
  flexShrink: 0,
};

const KICKER_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  fontSize: 9,
  letterSpacing: 'var(--tracking-caps, 0.08em)',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

const TITLE_STYLE: CSSProperties = {
  margin: '2px 0 0',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 23,
  color: 'var(--ink-1)',
};

const SUBTITLE_STYLE: CSSProperties = { margin: '3px 0 0', fontSize: 11.5, lineHeight: 1.45, color: 'var(--ink-2)' };

/** Capped and centred for the same reason `Standings`' body is (UI/UX audit **F-11**): a full-bleed
 * table on a desktop puts the name at one edge and the score at the other. */
const BODY_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
  padding: '12px 14px 78px',
  width: '100%',
  maxWidth: 880,
  marginInline: 'auto',
  boxSizing: 'border-box',
};

const NOTE_STYLE: CSSProperties = { fontSize: 12, lineHeight: 1.5, color: 'var(--ink-2)' };

const EMPTY_STATE_STYLE: CSSProperties = {
  minHeight: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
};

const EMPTY_STATE_PILL_STYLE: CSSProperties = {
  maxWidth: 420,
  padding: '8px 14px',
  borderRadius: 'var(--r-pill)',
  background: 'var(--hud-bg)',
  border: '1px solid var(--line-strong)',
  boxShadow: 'var(--shadow-hud)',
  fontFamily: 'var(--font-body)',
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--ink-2)',
};

const ALERT_STYLE: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  padding: '10px 12px',
  borderRadius: 'var(--r-md)',
  background: 'var(--danger-soft)',
  border: '1px solid var(--danger)',
  color: '#7E2C1B',
  fontSize: 12.5,
  lineHeight: 1.4,
};

const TABLE_STYLE: CSSProperties = {
  background: 'var(--surface-card)',
  border: '1.5px solid var(--line-strong)',
  borderRadius: 'var(--r-md)',
  boxShadow: 'var(--shadow-card)',
  overflow: 'hidden',
};

const TABLE_HEAD_ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 11px',
  background: 'var(--paper-1)',
  boxShadow: 'inset 0 -1px 0 var(--line)',
};

const COL_HEAD_STYLE: CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 800,
  fontSize: 8,
  letterSpacing: 'var(--tracking-caps, 0.08em)',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

/** A row is a button, because opening the prompt is what a row is *for*. ≥ 44px, UI.md §7's rule. */
const TABLE_ROW_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  minHeight: 52,
  padding: '8px 11px',
  border: 'none',
  borderTop: '1px solid var(--line)',
  cursor: 'pointer',
};

const PLACEMENT_STYLE: CSSProperties = {
  width: 34,
  flexShrink: 0,
  textAlign: 'left',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--ink-2)',
};

const NAME_STYLE: CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 13.5,
  color: 'var(--ink-1)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const SCRIPT_STYLE: CSSProperties = {
  display: 'block',
  fontFamily: 'var(--font-body)',
  fontSize: 10.5,
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const SCORE_STYLE: CSSProperties = {
  width: 64,
  flexShrink: 0,
  textAlign: 'right',
  fontFamily: 'var(--font-display)',
  fontWeight: 600,
  fontSize: 17,
  color: 'var(--accent-strong)',
  fontVariantNumeric: 'tabular-nums',
};

const PROMPT_STYLE: CSSProperties = {
  padding: '10px 12px 12px 45px',
  borderTop: '1px solid var(--line)',
  background: 'var(--paper-1)',
  fontFamily: 'var(--font-body)',
  fontSize: 12.5,
  lineHeight: 1.55,
  color: 'var(--ink-2)',
};

const BACK_BAR_STYLE: CSSProperties = {
  position: 'absolute',
  left: 0,
  right: 0,
  bottom: 0,
  height: 64,
  background: 'var(--hud-bg)',
  boxShadow: 'inset 0 1.5px 0 var(--hud-line)',
  display: 'flex',
  alignItems: 'center',
  padding: '0 14px',
};

const BACK_BUTTON_STYLE: CSSProperties = {
  flex: 1,
  minHeight: 44,
  borderRadius: 'var(--r-pill)',
  background: 'var(--accent-fill)',
  border: '1px solid var(--accent-strong)',
  color: '#FFFDF6',
  fontFamily: 'var(--font-display)',
  fontWeight: 500,
  fontSize: 14,
  cursor: 'pointer',
};
